import React from "react";
import PropTypes from "prop-types";

/**
 * A component that renders a horizontal scale bar with customizable labels, colors, and percentage indicators.
 * @returns {JSX.Element} Rendered scale bar component.
 */
export default function Scale({
  color,
  leftLabel,
  centreLabel,
  rightLabel,
  percentage,
  steps = 4,
  children,
  text_margin = 10
}) {
  // Ensures the percentage value is within 0-100 range
  percentage = Math.max(0, Math.min(percentage, 100));

  // Sets default color if not provided
  if (!color) {
    color = "button";
  }

  // Calculates the position of text within a safe range to avoid overflow
  const text_percentage = Math.max(
    text_margin,
    Math.min(percentage, 100 - text_margin)
  );
  return (
    <div className="scale" data-testid="scale-component">
      <div className="pl-3 pr-3">
        <div className={`bar inactive`} />
        <div
          className={`bar active bg-${color}`}
          style={{ width: `${percentage}%` }}
          data-testid="inactive-bar"
        />
        {/* <div
          className={`bar active bg-${color}`}
          style={{ width: `${percentage}%` }}
          data-testid="active-bar"
        /> */}
        <div className="lines" data-testid="step-lines">
          {Array.from({ length: steps - 1 }, (_, index) => (
            <div key={`step-line-${index + 1}`} />
          ))}
        </div>
        <div
          className={`circle bg-${color}-dark`}
          style={{ left: `${percentage}%` }}
          data-testid="circle-indicator"
        />
      </div>
      <div className="labels-container">
        <div className="labels-text-wrapper w-100">
          {leftLabel && (
            <div className="left-section">
              <span className="scale-label left">
                {leftLabel}
              </span>
              <span className="scale-label d-none left">Lower / </span>
            </div>
          )}
          <div className="center-section">
            {centreLabel && (
              <span className="scale-label centre">{centreLabel}</span>
            )}
          </div>
          <div className="right-section">
            {rightLabel && (
              <span className="scale-label right">{rightLabel}</span>
            )}
          </div>
        </div>

        <div
          className={`text`}
          style={{ left: `${text_percentage}%` }}
          data-testid="text-content"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// PropTypes definition for the ScaleBar component
Scale.propTypes = {
  /** Color theme for the active bar and circle (e.g., "primary", "secondary") */
  color: PropTypes.string,

  /** Label displayed on the left side of the scale */
  leftLabel: PropTypes.node,

  /** Label displayed in the center of the scale */
  centreLabel: PropTypes.node,

  /** Label displayed on the right side of the scale */
  rightLabel: PropTypes.node,

  /** The percentage value (0-100) determining the position of the indicator */
  percentage: PropTypes.number.isRequired,

  /** Number of step lines to display on the scale */
  steps: PropTypes.number,

  /** Content to display at the percentage position */
  children: PropTypes.node,

  /** Minimum margin (in percentage) from the edges for text positioning */
  text_margin: PropTypes.number
};
